/**
 * `system_config` — общий KV-стор глобальных параметров системы.
 *
 * Источник правды: миграция `supabase/migrations/20260504000000_geo_phase1.sql`
 * в репо `stryzh`. RLS: SELECT для всех authenticated, write только под
 * `current_user_role()='admin'`.
 *
 * `value` хранится как `text`; каст к нужному типу (int/float/bool) делает
 * читатель. Описание (description) показывает формат.
 */
import { createClient } from '@/api/supabase'
import { LEAD_OVERDUE_THRESHOLDS_MS } from '@/utils/format'

/** Ключи system_config с порогами SLA таймера лидов (значения — в минутах). */
export const LEAD_SLA_KEYS = {
  new: 'lead_sla_new_minutes',
  in_progress: 'lead_sla_in_progress_minutes',
  awaiting_payment: 'lead_sla_awaiting_payment_minutes',
} as const

/**
 * Пороги SLA таймера лидов из system_config, в миллисекундах по статусу.
 * Если ключ отсутствует/некорректен — fallback на дефолты из format.ts.
 */
export async function fetchLeadSlaThresholdsMs(): Promise<Record<string, number>> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('system_config')
    .select('key, value')
    .in('key', Object.values(LEAD_SLA_KEYS))
  if (error) throw new Error(error.message)
  const m = new Map(
    (data ?? []).map((r) => [(r as { key: string }).key, Number((r as { value: string }).value)]),
  )
  const toMs = (k: string, fallbackMs: number) => {
    const v = m.get(k)
    return v != null && Number.isFinite(v) && v > 0 ? v * 60_000 : fallbackMs
  }
  return {
    new: toMs(LEAD_SLA_KEYS.new, LEAD_OVERDUE_THRESHOLDS_MS.new),
    in_progress: toMs(LEAD_SLA_KEYS.in_progress, LEAD_OVERDUE_THRESHOLDS_MS.in_progress),
    awaiting_payment: toMs(
      LEAD_SLA_KEYS.awaiting_payment,
      LEAD_OVERDUE_THRESHOLDS_MS.awaiting_payment,
    ),
  }
}

export interface SystemConfigEntry {
  key: string
  value: string
  description: string | null
  updated_at: string
  updated_by: string | null
}

export async function listSystemConfig(): Promise<SystemConfigEntry[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('system_config')
    .select('key, value, description, updated_at, updated_by')
    .order('key', { ascending: true })
  if (error) throw new Error(error.message)
  return (data ?? []) as SystemConfigEntry[]
}

/**
 * Обновляет одно значение. updated_at/updated_by проставляются здесь же —
 * `auth.uid()` доступен через сессию supabase-js.
 */
export async function updateSystemConfigValue(
  key: string,
  value: string,
): Promise<SystemConfigEntry> {
  const supabase = createClient()
  const { data: userRes } = await supabase.auth.getUser()
  const uid = userRes.user?.id ?? null
  const { data, error } = await supabase
    .from('system_config')
    .update({
      value,
      updated_at: new Date().toISOString(),
      updated_by: uid,
    })
    .eq('key', key)
    .select('key, value, description, updated_at, updated_by')
    .single()
  if (error) throw new Error(error.message)
  return data as SystemConfigEntry
}
